import { useState, useLayoutEffect, useCallback } from "react";
import { Modal, useModalContext } from "../common/Modal";
import { TextInput } from "../common/TextInput";
import { gql, useMutation } from "@apollo/client";
import { ButtonGroup } from "../common/ButtonGroup";
import { Select } from "../common/Select";
import { useRequestManager } from "../common/RequestManager";
import { OrderPage_OrderFragment } from "../../../codegen/graphql";
import { CreateAfterSaleInput, UpdateAfterSaleInput } from "../../../codegen/graphql";
import { getTranslatedValue } from "../common/TranslatedValue";

interface AddOrEditAfterSaleModalProps {
  order: OrderPage_OrderFragment;
  afterSaleId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const AFTER_SALE_TYPE_OPTIONS = [
  { value: "CANCELLATION", label: "Cancellation" },
  { value: "NOT_RECEIVED", label: "Not Received" },
  { value: "REFUND", label: "Refund" },
  { value: "RETURN", label: "Return" },
];

const AFTER_SALE_STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

export default function AddOrEditAfterSaleModal({
  order,
  afterSaleId,
  onClose,
  onSuccess,
}: AddOrEditAfterSaleModalProps) {
  const isEditing = !!afterSaleId;

  return (
    <Modal
      onClose={onClose}
      title={isEditing ? "Edit After Sale" : "Add After Sale"}
      width="medium"
    >
      <AddOrEditAfterSaleModalContent
        order={order}
        afterSaleId={afterSaleId}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </Modal>
  );
}

function AddOrEditAfterSaleModalContent({
  order,
  afterSaleId,
  onClose,
  onSuccess,
}: AddOrEditAfterSaleModalProps) {
  const modalContext = useModalContext();
  const { trackRequest } = useRequestManager();
  const isEditing = !!afterSaleId;

  const existingAfterSale = isEditing && order.afterSales
    ? order.afterSales.find((a) => a.id === afterSaleId)
    : null;

  const [createAfterSale, { loading: createLoading }] = useMutation(
    CREATE_AFTER_SALE_MUTATION
  );
  const [updateAfterSale, { loading: updateLoading }] = useMutation(
    UPDATE_AFTER_SALE_MUTATION
  );

  const getInitialForm = useCallback((): CreateAfterSaleInput & UpdateAfterSaleInput => {
    if (existingAfterSale) {
      return {
        externalId: existingAfterSale.externalId ?? undefined,
        type: existingAfterSale.type ?? undefined,
        status: existingAfterSale.status ?? undefined,
        buyerMessage: existingAfterSale.buyerMessage ?? undefined,
        reason: existingAfterSale.reason ?? undefined,
        reasonCode: existingAfterSale.reasonCode ?? undefined,
        orderId: existingAfterSale.orderId ?? undefined,
        lineItemId: existingAfterSale.lineItemId ?? undefined,
        lineItemQuantity: existingAfterSale.lineItemQuantity ?? undefined,
        restockedQuantity: existingAfterSale.restockedQuantity ?? undefined,
        notRestockedReason: existingAfterSale.notRestockedReason ?? undefined,
        requestedAt: existingAfterSale.requestedAt ?? undefined,
      };
    }
    return {
      orderId: order.id,
      type: undefined,
      status: undefined,
      externalId: undefined,
      buyerMessage: undefined,
      reason: undefined,
      reasonCode: undefined,
      lineItemId: undefined,
      lineItemQuantity: undefined,
      restockedQuantity: undefined,
      notRestockedReason: undefined,
      requestedAt: undefined,
    };
  }, [existingAfterSale, order.id]);

  const [form, setForm] = useState<CreateAfterSaleInput & UpdateAfterSaleInput>(getInitialForm);

  const handleChange = useCallback((name: string, value: string | number | undefined) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      const numFields = [
        "lineItemQuantity",
        "restockedQuantity",
      ];
      if (numFields.includes(name)) {
        const parsed = value === "" ? undefined : Number(value);
        handleChange(name, isNaN(Number(parsed)) ? undefined : parsed);
      } else {
        handleChange(name, value || undefined);
      }
    },
    [handleChange]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (isEditing && afterSaleId) {
        await trackRequest(
          updateAfterSale({
            variables: { id: afterSaleId, input: form },
          }),
          { title: "Updating after sale", isBlocking: true }
        );
      } else {
        await trackRequest(
          createAfterSale({
            variables: { input: { ...form, orderId: order.id } },
          }),
          { title: "Creating after sale", isBlocking: true }
        );
      }
      onSuccess();
    },
    [form, isEditing, afterSaleId, order.id, updateAfterSale, createAfterSale, trackRequest, onSuccess]
  );

  useLayoutEffect(() => {
    modalContext.setActions([
      { label: "Cancel", onClick: onClose, preset: "white" },
      {
        label: isEditing ? "Update" : "Add",
        onClick: handleSubmit,
        disabled: createLoading || updateLoading,
      },
    ]);
  }, [onClose, handleSubmit, isEditing, createLoading, updateLoading, modalContext]);

  const lineItemOptions = [
    { value: "", label: "None" },
    ...(order.orderProductsVariations?.map((item) => ({
      value: item.id,
      label: item.adVariation?.ad?.title
        ? `${getTranslatedValue(item.adVariation.ad.title)} (qty ${item.quantity})`
        : `Line ${item.id}`,
    })) ?? []),
  ];

  return (
    <div className="space-y-4">
      <ButtonGroup
        label="Type"
        name="type"
        options={AFTER_SALE_TYPE_OPTIONS}
        value={form.type ?? null}
        onChange={(value) => handleChange("type", value)}
      />

      <ButtonGroup
        label="Status"
        name="status"
        options={AFTER_SALE_STATUS_OPTIONS}
        value={form.status ?? null}
        onChange={(value) => handleChange("status", value)}
      />

      <TextInput
        label="External ID"
        name="externalId"
        value={form.externalId ?? ""}
        onChange={handleInputChange}
        placeholder="External ID"
      />

      <Select
        label="Line item"
        name="lineItemId"
        value={form.lineItemId ?? ""}
        onChange={(value) => handleChange("lineItemId", value || undefined)}
        options={lineItemOptions}
        placeholder="Select line item"
      />

      <TextInput
        label="Returned quantity"
        name="lineItemQuantity"
        type="number"
        value={form.lineItemQuantity ?? ""}
        onChange={handleInputChange}
      />

      <TextInput
        label="Restocked quantity"
        name="restockedQuantity"
        type="number"
        value={form.restockedQuantity ?? ""}
        onChange={handleInputChange}
      />

      <TextInput
        label="Reason"
        name="reason"
        value={form.reason ?? ""}
        onChange={handleInputChange}
      />

      <TextInput
        label="Reason code"
        name="reasonCode"
        value={form.reasonCode ?? ""}
        onChange={handleInputChange}
      />

      <TextInput
        label="Buyer message"
        name="buyerMessage"
        value={form.buyerMessage ?? ""}
        onChange={handleInputChange}
        multiline
        rows={2}
      />

      <TextInput
        label="Not restocked reason"
        name="notRestockedReason"
        value={form.notRestockedReason ?? ""}
        onChange={handleInputChange}
      />

      <TextInput
        label="Requested at (ISO datetime)"
        name="requestedAt"
        value={form.requestedAt ?? ""}
        onChange={handleInputChange}
        placeholder="e.g. 2025-03-07T12:00:00.000Z"
      />
    </div>
  );
}

const AFTER_SALE_FIELDS = gql`
  fragment AddOrEditAfterSaleModal_AfterSale on AfterSale {
    id
    externalId
    type
    status
    buyerMessage
    reason
    reasonCode
    orderId
    lineItemId
    lineItemQuantity
    restockedQuantity
    notRestockedReason
    returnPackageId
    requestedAt
  }
`;

const CREATE_AFTER_SALE_MUTATION = gql`
  mutation AddOrEditAfterSaleModal_CreateAfterSale($input: CreateAfterSaleInput!) {
    createAfterSale(input: $input) {
      ...AddOrEditAfterSaleModal_AfterSale
    }
  }
  ${AFTER_SALE_FIELDS}
`;

const UPDATE_AFTER_SALE_MUTATION = gql`
  mutation AddOrEditAfterSaleModal_UpdateAfterSale($id: ID!, $input: UpdateAfterSaleInput!) {
    updateAfterSale(id: $id, input: $input) {
      ...AddOrEditAfterSaleModal_AfterSale
    }
  }
  ${AFTER_SALE_FIELDS}
`;
